import { createClient } from '@/utils/supabase/client';
import { toast } from 'sonner';

export const isValidRedirectUrl = (url: string): boolean => {
  try {
    return url.startsWith('/') && !url.startsWith('//');
  } catch {
    return false;
  }
};

// Helper function to create a timeout promise
const createTimeout = (ms: number): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Operation timed out')), ms);
  });
};

// Helper function to make a fetch request with timeout
const fetchWithTimeout = async (url: string, options: RequestInit, timeoutMs: number = 10000) => {
  return await Promise.race([
    fetch(url, options),
    createTimeout(timeoutMs)
  ]);
};

export async function logoutAndRedirect(options: { redirectTo?: string } = {}) {
  const { redirectTo = '/login' } = options;
  const safeRedirectTo = isValidRedirectUrl(redirectTo) ? redirectTo : '/login';
  
  console.log('Starting logout process...');
  
  // Show initial loading toast
  const loadingToast = toast.loading('Signing out...', {
    description: 'Please wait while we log you out securely',
  });
  
  try {
    // Set up a maximum timeout for the entire logout process (10 seconds)
    await Promise.race([
      performLogout(),
      createTimeout(10000)
    ]);
  } catch (error) {
    console.error('Logout process failed or timed out:', error);
    
    // Dismiss loading toast and show error
    toast.dismiss(loadingToast);
    
    const isTimeout = error instanceof Error && error.message.includes('timed out');
    
    toast.error(isTimeout ? 'Logout taking too long' : 'Logout error occurred', {
      description: isTimeout 
        ? 'Logout is taking longer than expected. You will be redirected for security.'
        : 'There was an issue signing you out. You will be redirected to the login page for security.',
      duration: 4000,
    });
    
    // Emergency redirect after error/timeout
    setTimeout(() => {
      console.log('Emergency redirect due to logout timeout/error');
      window.location.href = safeRedirectTo;
    }, 1500);
    return;
  }

  async function performLogout() {
    // Try server-side logout API with timeout
    let serverLogoutSuccess = false;
    try {
      console.log('Attempting server-side logout...');
      const response = await fetchWithTimeout('/api/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }, 6000); // 6 second timeout for server call
      
      serverLogoutSuccess = response.ok;
      if (serverLogoutSuccess) {
        console.log('Server-side logout successful');
      } else {
        console.warn('Server-side logout failed with status:', response.status);
      }
    } catch (serverError) {
      console.warn('Server-side logout failed or timed out:', serverError);
    }
    
    // Client-side logout with Supabase and timeout
    let clientLogoutSuccess = false;
    try {
      console.log('Attempting client-side logout...');
      const supabase = createClient();
      
      const { error } = await Promise.race([
        supabase.auth.signOut({ scope: 'global' }),
        createTimeout(4000) // 4 second timeout for Supabase
      ]);
      
      clientLogoutSuccess = !error;
      if (clientLogoutSuccess) {
        console.log('Supabase logout successful');
      } else {
        console.error('Supabase logout error:', error);
      }
    } catch (clientError) {
      console.warn('Client-side logout failed or timed out:', clientError);
    }
    
    // Clear localStorage items
    if (typeof window !== 'undefined') {
      try {
        ['rentwise-user-preferences', 'rentwise-theme-preference', 'rentwise-dashboard-layout']
          .forEach(key => {
            try {
              localStorage.removeItem(key);
            } catch (e) {
              console.warn(`Failed to remove ${key}:`, e);
            }
          });
        console.log('localStorage cleanup completed');
      } catch (e) {
        console.warn('localStorage cleanup failed:', e);
      }
    }
      
    // Dismiss loading toast and show result
    toast.dismiss(loadingToast);
    
    if (!serverLogoutSuccess && !clientLogoutSuccess) {
      toast.warning('Signed out with warnings', {
        description: 'Some session data may not have been cleared properly.',
        duration: 4000,
      });
    } else {
      toast.success('Successfully signed out', {
        description: 'You have been logged out securely. Redirecting...',
        duration: 2000,
      });
    }
    
    // Redirect after success
    setTimeout(() => {
      console.log(`Redirecting to: ${safeRedirectTo}`);
      window.location.href = safeRedirectTo;
    }, 1000);
  }
}

export async function isUserAuthenticated(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  
  try {
    const supabase = createClient();
    const { data: { user }, error } = await supabase.auth.getUser();
    return !error && !!user?.id;
  } catch {
    return false;
  }
}

export async function getUserProfile() {
  if (typeof window === 'undefined') return null;
  
  try {
    const supabase = createClient();
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) return null;
    
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, user_type, name, email, country, avatar_url')
      .eq('id', user.id)
      .single();
      
    return profileError ? null : profile;
  } catch {
    return null;
  }
}

 